import { useCallback, useContext } from 'react';

import AuthContext from '../auth/auth-service';

import {
  ApiKey,
  ApiKeySession,
  ApiKeyWithSecret,
  createApiKey as createApiKeyRequest,
  deleteApiKey as deleteApiKeyRequest,
  getApiKeySessions as getApiKeySessionsRequest,
  listApiKeys as listApiKeysRequest,
} from './api-keys-service';

/**
 * Binds the pure api-keys-service functions to the current accessToken from
 * AuthContext and exposes the short signatures the UI consumes. The service
 * itself stays token-agnostic and unit-testable.
 */
export const useApiKeys = () => {
  const { accessToken } = useContext(AuthContext);

  const listApiKeys = useCallback((): Promise<ApiKey[]> => {
    return listApiKeysRequest(accessToken ?? '');
  }, [accessToken]);

  const createApiKey = useCallback(
    (name: string, permissions: string[]): Promise<ApiKeyWithSecret> => {
      return createApiKeyRequest(accessToken ?? '', name, permissions);
    },
    [accessToken],
  );

  const deleteApiKey = useCallback(
    (uuid: string): Promise<void> => {
      return deleteApiKeyRequest(accessToken ?? '', uuid);
    },
    [accessToken],
  );

  const getApiKeySessions = useCallback(
    (uuid: string): Promise<ApiKeySession[]> => {
      return getApiKeySessionsRequest(accessToken ?? '', uuid);
    },
    [accessToken],
  );

  return { listApiKeys, createApiKey, deleteApiKey, getApiKeySessions };
};

export default useApiKeys;
